import React from 'react'
import { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ModuleCardProps {
  icon: LucideIcon
  title: string
  description: string
  isSelected: boolean
  onClick: () => void
  disabled?: boolean
  /**
   * Announced but not creatable yet. Renders muted and inert — not just
   * `disabled`, which also covers the transient "submitting" state and would
   * make a coming-soon module look like a momentarily blocked one.
   */
  unavailable?: boolean
  /** Small label next to the title, e.g. "Coming soon". */
  badge?: React.ReactNode
  className?: string
}

export const ModuleCard: React.FC<ModuleCardProps> = ({
  icon: Icon,
  title,
  description,
  isSelected,
  onClick,
  disabled = false,
  unavailable = false,
  badge,
  className
}) => {
  const selected = isSelected && !unavailable
  return (
    <button
      type="button"
      onClick={unavailable ? undefined : onClick}
      disabled={disabled || unavailable}
      aria-disabled={disabled || unavailable || undefined}
      aria-pressed={unavailable ? undefined : selected}
      className={cn(
        'relative flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left',
        'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-2',
        unavailable
          ? 'border-dashed border-gray-200 bg-gray-50 cursor-not-allowed'
          : selected
            ? 'border-primary bg-primary-50 hover:shadow-sm'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
        disabled && !unavailable && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center transition-all',
          unavailable
            ? 'bg-gray-100 text-gray-400'
            : selected
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-600'
        )}
      >
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <div className={cn('font-medium mb-1', unavailable ? 'text-gray-600' : 'text-gray-900')}>
          {title}
        </div>
        {/* Two lines of room, never a clamp: the copy is written to fit two lines,
            and a clamp would hide the end of any description that does not.
            `min-h-8` is two `text-xs` lines (2 × 1rem), so cards stay aligned. */}
        <div data-module-description className="min-h-8 text-xs text-gray-500">
          {description}
        </div>
        {/* Below the text rather than beside the title: next to a long title it
            wrapped on some cards and not others, and the grid lost its rhythm. */}
        {badge && <div className="mt-2">{badge}</div>}
      </div>
      {selected && (
        <div className="absolute top-3 right-3">
          <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
            <svg
              className="h-3 w-3 text-white"
              fill="none"
              viewBox="0 0 12 12"
            >
              <path
                d="M10 3L4.5 8.5L2 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}
    </button>
  )
}
